import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { ReactTable, NoContent } from '../../components/shared';
import LoadingSpinner from '../../components/LoadingSpinner';
import NewFilterDrawer from '../../components/shared/FilterDrawer/NewFilterDrawer';
import { useGetUnmappedDataFeedQuery } from '../../services/UnmappedDataFeed/UnmappedDataFeedService';
import { useLazyGetUnmappedDataFeedSourcesQuery } from '../../services/UnmappedDataFeed/UnmappedDataFeedSourcesService';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';
import { UNMAPPED_DATA_FEED } from '../../constants/tableColumns';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../constants/FilterConstants';
import useSearchFilterParam from '../../hooks/useSearchFilterParam';
import useStyles from './style';
import { formatDate, useSortBy, useHasSortableColumns, useExceptionReportingHandlers } from './utils';
import { useDispatch, useSelector } from 'react-redux';
import { resetUnmappedDataFeedFilters, setUnmappedDataFeedFilters } from '../../redux-slice/exceptionReportingFilters';

const SORT_DEFAULT_VALUE = 'source:ASC';

const UnmappedDataFeedTab = ({ onCountChange }) => {
  const classes = useStyles();
  const { DRAWER_TYPES, TITLE, FILTER_DATA } = FILTER_CONSTANTS;
  const dispatch = useDispatch();
  const { unmappedDataFeed } = useSelector((state) => state.exceptionReportingFilter);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const [sort, setSort] = useState(SORT_DEFAULT_VALUE);
  const [filterParams, setFilterParams] = useState(FILTER_CONSTANTS.FILTER_DATA.UNMAPPED_DATA_FEED.INITIAL_PARAMS);

  const [getUnmappedDataFeedSources] = useLazyGetUnmappedDataFeedSourcesQuery();

  useEffect(() => {
    const storedFilterParams = { ...FILTER_DATA.UNMAPPED_DATA_FEED.INITIAL_PARAMS };
    storedFilterParams[FILTER_VALUES.SOURCE] = unmappedDataFeed[FILTER_VALUES.SOURCE] ?? [];
    storedFilterParams[FILTER_VALUES.DATE_RANGE_FILTER] = unmappedDataFeed[FILTER_VALUES.DATE_RANGE_FILTER] ?? {
      from: '',
      to: '',
    };
    setSort(unmappedDataFeed.sort || SORT_DEFAULT_VALUE);
    setCurrentPage(unmappedDataFeed.pageNumber || DEFAULT_PAGE);
    setPerPage(unmappedDataFeed.rowsPerPage || DEFAULT_PER_PAGE);
    setFilterParams(storedFilterParams);
  }, [
    unmappedDataFeed.sort,
    unmappedDataFeed.pageNumber,
    unmappedDataFeed.rowsPerPage,
    unmappedDataFeed[FILTER_VALUES.SOURCE],
    unmappedDataFeed[FILTER_VALUES.DATE_RANGE_FILTER],
  ]);

  const getAPI = useCallback(
    (value) => {
      switch (value) {
        case FILTER_VALUES.SOURCE:
          return getUnmappedDataFeedSources;
        default:
          return null;
      }
    },
    [getUnmappedDataFeedSources],
  );

  const filterData = useMemo(
    () =>
      FILTER_CONSTANTS.FILTER_DATA.UNMAPPED_DATA_FEED.FILTER.map((item) => {
        const api = getAPI(item.value);
        return {
          ...item,
          api,
        };
      }),
    [getAPI],
  );

  const searchParams = useSearchFilterParam({
    filterKey: FILTER_CONSTANTS.FILTER_DATA.UNMAPPED_DATA_FEED,
    filterParams,
    sort,
  });

  const { data, isLoading, isFetching, isError, error } = useGetUnmappedDataFeedQuery({
    page: currentPage,
    perPage,
    searchParams,
  });

  useEffect(() => {
    if (isError) {
      onCountChange(0);
      return;
    }

    if (data?.feedMappingIssues) {
      onCountChange(data.pagination.total);
    }
  }, [data?.pagination?.total, onCountChange, isError]);

  const columns = useMemo(() => UNMAPPED_DATA_FEED(), []);

  const hasSortableColumns = useHasSortableColumns(columns);

  const sortBy = useSortBy(sort, SORT_DEFAULT_VALUE);

  const tableData = useMemo(() => {
    if (!data || !data.feedMappingIssues) {
      return [];
    }

    const issues = data?.feedMappingIssues || [];

    return issues.map((value) => {
      const fromDate = formatDate(value.date_affected_from);
      const toDate = formatDate(value.date_affected_to);

      const dateRange = fromDate === 'N/A' && toDate === 'N/A' ? 'N/A' : `${fromDate} - ${toDate}`;

      return {
        source: value.source || '',
        rows_affected: value.rows_affected || 0,
        amount_affected: value.amount_affected ? `$${Number(value.amount_affected).toFixed(2)}` : '$0.00',
        map_confidence: value.map_confidence ?? 0,
        unmapped_scenario: value.unmapped_scenario || '',
        date_range: dateRange,
        sublocation: value.sublocation || '',
        asset: value.asset_id || '',
        modem: value.modem_id || '',
        alert: value.alert || {},
      };
    });
  }, [data]);

  const { applyFilterHandler, resetFilterParams, handleSortChangeWithRedux, handlePageChange, handlePerPageChange } =
    useExceptionReportingHandlers({
      dispatch,
      reduxState: unmappedDataFeed,
      setFilterAction: setUnmappedDataFeedFilters,
      resetFilterAction: resetUnmappedDataFeedFilters,
      initialFilterParams: FILTER_DATA.UNMAPPED_DATA_FEED.INITIAL_PARAMS,
      sortDefaultValue: SORT_DEFAULT_VALUE,
      setFilterParams,
      setSort,
      setCurrentPage,
      setPerPage,
    });

  return (
    <>
      <Box className={classes.searchFilterWrap}>
        <NewFilterDrawer
          filteredValues={filterParams}
          name={TITLE.FILTERS.toUpperCase()}
          title={TITLE.FILTERS}
          drawerType={DRAWER_TYPES.FILTER_DRAWER}
          onReset={resetFilterParams}
          onApplyFilter={applyFilterHandler}
          filterData={filterData}
          emptyFilterLength={JSON.stringify(FILTER_CONSTANTS.FILTER_DATA.UNMAPPED_DATA_FEED.INITIAL_PARAMS).length}
        />
      </Box>
      <Box>
        {isLoading || isFetching ? (
          <LoadingSpinner />
        ) : isError ? (
          <NoContent
            title="No Unmapped Data Feed Found"
            desc={error?.data?.message || 'There are no unmapped data feeds available with the current filters.'}
          />
        ) : tableData.length === 0 ? (
          <NoContent
            title="No Unmapped Data Feed Found"
            desc="No unmapped data feeds match your current search criteria."
          />
        ) : (
          <Box className={hasSortableColumns ? classes.fixedWidthTable : ''}>
            <ReactTable
              columns={columns}
              data={tableData}
              pagination={true}
              manualPagination={true}
              pageNumberForTablePagination={currentPage - 1}
              perPage={perPage}
              totalCount={data?.pagination?.total || 0}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              isLoading={isFetching}
              sortable={true}
              onSortChange={handleSortChangeWithRedux}
              sortBy={sortBy}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default UnmappedDataFeedTab;
